import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { BookingConfirmation } from "@/components/booking/BookingConfirmation";
import { PersonalInfoFields } from "@/components/booking/PersonalInfoFields";
import { LocationFields } from "@/components/booking/LocationFields";
import { ContactMethodFields } from "@/components/booking/ContactMethodFields";
import { ServiceSelectionFields } from "@/components/booking/ServiceSelectionFields";
import type { Rate, Therapist } from "@/types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM_DATA = {
  name: "",
  contact_number: "",
  hotel_name: "",
  room_number: "",
  line_id: "",
  whatsapp_number: "",
  service_type: "",
  duration_minutes: "",
  preferred_therapist: "",
  booking_datetime: "",
  special_requests: "",
  is_now_booking: "false",
  preferred_language: "en",
};

const BookingForm = () => {
  const [searchParams] = useSearchParams();
  const [rates, setRates] = useState<Rate[]>([]);
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [confirmedBooking, setConfirmedBooking] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ratesRes, therapistsRes] = await Promise.all([
          axios.get(`${API}/rates`),
          axios.get(`${API}/therapists`),
        ]);
        setRates(ratesRes.data);
        setTherapists(therapistsRes.data);
      } catch {
        toast.error("Failed to load service data");
      }
    };
    fetchData();
  }, []);

  // Pre-select therapist from URL query param
  useEffect(() => {
    const therapistParam = searchParams.get("therapist");
    if (therapistParam && therapists.length > 0) {
      const match = therapists.find(
        (th) => th.name.toLowerCase() === therapistParam.toLowerCase()
      );
      if (match) {
        setFormData((prev) => ({ ...prev, preferred_therapist: match.name }));
      }
    }
  }, [searchParams, therapists]);

  // Update price display when service or duration changes
  useEffect(() => {
    if (formData.service_type && formData.duration_minutes) {
      const rate = rates.find(
        (r) =>
          r.service_type === formData.service_type &&
          r.duration_minutes === parseInt(formData.duration_minutes)
      );
      setSelectedPrice(rate ? rate.price_thb : null);
    } else {
      setSelectedPrice(null);
    }
  }, [formData.service_type, formData.duration_minutes, rates]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name || !formData.contact_number || !formData.hotel_name ||
      !formData.room_number || !formData.service_type || !formData.duration_minutes
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.line_id && !formData.whatsapp_number) {
      toast.error("Please provide either Line ID or WhatsApp number");
      return;
    }

    setLoading(true);
    try {
      const bookingData: any = {
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes),
        is_now_booking: formData.is_now_booking === "true",
      };

      const luid = searchParams.get("luid");
      if (luid) bookingData.customer_line_user_id = luid;

      const response = await axios.post(`${API}/bookings`, bookingData);
      const chatUrl = `${window.location.origin}/chat/${response.data.id}?token=${response.data.chat_token}`;
      setConfirmedBooking({ ...response.data, chatUrl, is_now_booking: formData.is_now_booking === "true" });

      toast.success(`Booking confirmed! Total: ฿${response.data.price_thb}.`, { duration: 6000 });

      // Taxi surcharge notifications
      if (response.data.taxi_surcharge && response.data.taxi_surcharge > 0) {
        setTimeout(() => {
          toast.warning(
            `Your location is ${response.data.distance_km}km from Sukhumvit 20. A taxi surcharge of ${response.data.taxi_surcharge} THB will be added.`,
            { duration: 15000 }
          );
        }, 1500);
      } else if (response.data.taxi_surcharge === -1) {
        setTimeout(() => {
          toast.warning(
            `Your location is ${response.data.distance_km}km from Sukhumvit 20. The travel surcharge will be discussed before your session.`,
            { duration: 15000 }
          );
        }, 1500);
      }

      if (response.data.reassignment_message) {
        setTimeout(() => toast.info(response.data.reassignment_message, { duration: 12000 }), 2000);
      }

      if (formData.whatsapp_number) {
        setTimeout(() => {
          toast.info(
            <div>
              <p>Contact us on WhatsApp:</p>
              <a
                href={`https://wa.me/${formData.whatsapp_number.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 underline"
              >
                Click to open WhatsApp
              </a>
            </div>,
            { duration: 10000 }
          );
        }, 1000);
      }

      setFormData(INITIAL_FORM_DATA);
      setSelectedPrice(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const serviceTypes = Array.from(new Set(rates.map((r) => r.service_type)));
  const durations = formData.service_type
    ? Array.from(new Set(rates.filter((r) => r.service_type === formData.service_type).map((r) => r.duration_minutes)))
    : [];

  return (
    <section className="mt-16" id="booking">
      {confirmedBooking ? (
        <BookingConfirmation
          booking={confirmedBooking}
          onBookAnother={() => setConfirmedBooking(null)}
        />
      ) : (
        <Card className="max-w-2xl mx-auto shadow-lg" data-testid="booking-form-card">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl">{t("booking.title")}</CardTitle>
            <CardDescription className="text-base mt-2">
              {t("booking.formDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
              <PersonalInfoFields
                name={formData.name}
                contactNumber={formData.contact_number}
                preferredLanguage={formData.preferred_language}
                onChange={handleChange}
                t={t}
              />
              <LocationFields
                hotelName={formData.hotel_name}
                roomNumber={formData.room_number}
                onChange={handleChange}
                t={t}
              />
              <ContactMethodFields
                lineId={formData.line_id}
                whatsappNumber={formData.whatsapp_number}
                onChange={handleChange}
                t={t}
              />
              <ServiceSelectionFields
                serviceType={formData.service_type}
                durationMinutes={formData.duration_minutes}
                preferredTherapist={formData.preferred_therapist}
                bookingDatetime={formData.booking_datetime}
                specialRequests={formData.special_requests}
                selectedPrice={selectedPrice}
                serviceTypes={serviceTypes}
                durations={durations}
                therapists={therapists}
                onChange={handleChange}
                t={t}
                isNowBooking={formData.is_now_booking === "true"}
              />
              <Button
                type="submit"
                className="w-full text-lg py-6"
                disabled={loading}
                data-testid="submit-booking-button"
              >
                {loading ? t("booking.submitting") : t("booking.submitBooking")}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </section>
  );
};

export default BookingForm;
